import { useState } from 'react';
import { useUsers, useUpdateUser } from '../../hooks/useUsers.js';
import { useError } from '../../components/shared/ErrorBanner.jsx';
import Modal from '../../components/shared/Modal.jsx';
import { centsToUsd } from '../../lib/format.js';
import RateHistoryTimeline from '../../components/admin/RateHistoryTimeline.jsx';

export default function CrewPage() {
  const { data: users = [] } = useUsers();
  const [editing, setEditing] = useState(null);

  return (
    <div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Name</th><th>Role</th><th className="col-meta">Type</th>
            <th className="num">Rate</th><th className="num col-meta">OT @</th>
            <th className="col-meta">Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.discord_id}>
              <td>{u.display_name}</td>
              <td><span className={'badge' + (u.role === 'admin' ? ' orange' : '')}>{u.role}</span></td>
              <td className="col-meta"><span className="badge">{u.employment_type}</span></td>
              <td className="num">{u.hourly_rate_cents == null ? '—' : `${centsToUsd(u.hourly_rate_cents)}/h`}</td>
              <td className="num col-meta">{u.ot_threshold_hrs == null ? '—' : `${u.ot_threshold_hrs}h × ${u.ot_multiplier}`}</td>
              <td className="col-meta">{u.active ? 'active' : <span style={{ color: 'var(--warm-gray)' }}>inactive</span>}</td>
              <td style={{ textAlign: 'right' }}>
                <button className="btn-sm" onClick={() => setEditing(u)}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && (
        <UserEditModal user={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function UserEditModal({ user, onClose }) {
  const [rate, setRate]     = useState((user.hourly_rate_cents / 100).toFixed(2));
  const [thr, setThr]       = useState(user.ot_threshold_hrs);
  const [mult, setMult]     = useState(user.ot_multiplier);
  const [type, setType]     = useState(user.employment_type);
  const [role, setRole]     = useState(user.role);
  const [active, setActive] = useState(!!user.active);
  const [reason, setReason] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(''); // yyyy-mm-dd; '' = today
  const [supervised, setSupervised] = useState(() => {
    if (!user.foreman_user_ids) return [];
    if (Array.isArray(user.foreman_user_ids)) return user.foreman_user_ids;
    try { return JSON.parse(user.foreman_user_ids) || []; } catch { return []; }
  });
  const { data: allUsers = [] } = useUsers();
  const update = useUpdateUser();
  const { show } = useError();

  const rateChanging =
    Math.round(Number(rate) * 100) !== user.hourly_rate_cents ||
    Number(thr) !== user.ot_threshold_hrs ||
    Number(mult) !== user.ot_multiplier;

  const save = async (e) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        id: user.discord_id,
        hourly_rate_cents: Math.round(Number(rate) * 100),
        ot_threshold_hrs: Number(thr),
        ot_multiplier: Number(mult),
        employment_type: type,
        role, active,
        // Server clears this if role !== 'foreman'; sending always is fine.
        foreman_user_ids: role === 'foreman' ? supervised : null,
        reason: reason || null,
        // Only send effective_from if rate fields are actually changing AND
        // admin chose a non-default date. Server defaults to "now" otherwise.
        effective_from: (rateChanging && effectiveFrom) ? effectiveFrom : null,
      });
      onClose();
    } catch (err) { show(err.message); }
  };

  const toggleSupervised = (id) => {
    setSupervised(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <Modal onClose={onClose} title={user.display_name} icon="ph-user-circle-gear">
      <form onSubmit={save}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="field">
            <label className="field-label">Hourly rate ($)</label>
            <input className="field-input" type="number" step="0.01" min="0"
              value={rate} onChange={e => setRate(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Employment</label>
            <select className="field-select" value={type} onChange={e => setType(e.target.value)}>
              <option value="w2">W-2</option>
              <option value="1099">1099</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">OT threshold (hrs/wk)</label>
            <input className="field-input" type="number" step="1" min="0" max="168"
              value={thr} onChange={e => setThr(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">OT multiplier</label>
            <input className="field-input" type="number" step="0.1" min="1" max="3"
              value={mult} onChange={e => setMult(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Role</label>
            <select className="field-select" value={role} onChange={e => setRole(e.target.value)}>
              <option value="crew">crew</option>
              <option value="foreman">foreman</option>
              <option value="bookkeeper">bookkeeper</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">Active</label>
            <select className="field-select" value={active ? '1' : '0'}
              onChange={e => setActive(e.target.value === '1')}>
              <option value="1">active</option>
              <option value="0">inactive</option>
            </select>
          </div>
        </div>
        {role === 'foreman' && (
          <div className="field">
            <label className="field-label">Supervised crew</label>
            <div style={{
              maxHeight: 180, overflow: 'auto',
              border: '1px solid var(--surface-container-high)',
              padding: 8, background: 'var(--surface-container)',
            }}>
              {allUsers.filter(u => u.discord_id !== user.discord_id).map(u => (
                <label key={u.discord_id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '4px 0', fontSize: 13, cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={supervised.includes(u.discord_id)}
                    onChange={() => toggleSupervised(u.discord_id)}
                  />
                  <span>{u.display_name}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--warm-gray)' }}>{u.role}</span>
                </label>
              ))}
            </div>
            <div style={{
              fontSize: 11, color: 'var(--warm-gray)', marginTop: 4,
              fontFamily: 'var(--font-body)', letterSpacing: 'normal', textTransform: 'none',
            }}>
              {supervised.length} selected. Foreman will see their timesheets, JHA, and can correct their entries.
            </div>
          </div>
        )}
        {rateChanging && (
          <div className="field">
            <label className="field-label">
              Effective from (optional — leave empty for "now")
            </label>
            <input className="field-input" type="date"
              value={effectiveFrom}
              onChange={e => setEffectiveFrom(e.target.value)}
              max={new Date().toISOString().slice(0, 10)} />
            <div style={{
              fontSize: 11, color: 'var(--warm-gray)', marginTop: 4,
              fontFamily: 'var(--font-body)', letterSpacing: 'normal', textTransform: 'none',
            }}>
              For backdated corrections: e.g. "this rate should have been in effect since Mar 15".
              Replaces all rate history from that date forward.
            </div>
          </div>
        )}
        <div className="field">
          <label className="field-label">Reason (optional, but recommended)</label>
          <input className="field-input" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. annual raise, role change, departure…" />
        </div>
        <button className="btn-sm primary" type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>

      <div style={{ marginTop: 24 }}>
        <div style={{
          fontFamily: 'var(--font-label)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.1rem', textTransform: 'uppercase',
          color: 'var(--orange)', marginBottom: 8,
        }}>Rate history</div>
        <RateHistoryTimeline userId={user.discord_id} />
      </div>
    </Modal>
  );
}
